/**
 * ReceiptsPage — receipt list and the mismatch viewer.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05C. Receipts are the local
 * source of truth this campaign is built around, so this page treats them as
 * read-only evidence: it lists the envelopes C03C's mounted `/v1/receipts`
 * routes return (this is the one surface here talking to a route that is
 * already mounted) and, for a selected envelope, renders each server-reported
 * mismatch as `expected` beside `observed` — a viewer, never a resolver.
 * There is no button below that edits, merges, or "fixes" a mismatch;
 * reconciliation belongs to the receipt owner, not to this cockpit.
 *
 * UNAVAILABLE IS HONEST: a 404/503 renders an explicit Unavailable state —
 * never a fabricated envelope and never a synthesized mismatch.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';

import {
  OperationsApiError,
  fetchReceiptMismatches,
  fetchReceipts,
} from '../../api/operations';

function ReceiptsPage() {
  const [receipts, setReceipts] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState('');
  const [mismatches, setMismatches] = useState([]);
  const [mismatchError, setMismatchError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await fetchReceipts();
      setReceipts(response.envelopes || []);
    } catch (err) {
      if (err instanceof OperationsApiError && err.isUnavailable) {
        setReceipts([]);
        setError('unavailable');
      } else {
        setReceipts([]);
        setError(err instanceof Error ? err.message : 'the receipt list could not be loaded');
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const select = useCallback(async (envelopeId) => {
    setSelected(envelopeId);
    setMismatches([]);
    setMismatchError(null);
    if (!envelopeId) return;
    try {
      const response = await fetchReceiptMismatches(envelopeId);
      setMismatches(response.mismatches || []);
    } catch (err) {
      if (err instanceof OperationsApiError && err.isUnavailable) {
        setMismatchError('unavailable');
      } else {
        setMismatchError(err instanceof Error ? err.message : 'mismatches could not be loaded');
      }
    }
  }, []);

  if (error === 'unavailable') {
    return (
      <section>
        <h1>Receipts</h1>
        <p className="unavailable">
          Unavailable — the receipt sync backend is not mounted for this deployment, so no
          envelope can be listed. Nothing here is fabricated.
        </p>
      </section>
    );
  }

  if (receipts === null) {
    return (
      <section>
        <h1>Receipts</h1>
        <p>Loading…</p>
      </section>
    );
  }

  return (
    <section>
      <h1>Receipts</h1>
      {error ? <p className="error">{error}</p> : null}
      {receipts.length === 0 ? <p>No receipt envelopes were reported by the server.</p> : null}
      <table>
        <thead>
          <tr>
            <th>Envelope</th>
            <th>Source ref</th>
            <th>Body digest</th>
            <th>Synced at</th>
            <th>Mismatches</th>
          </tr>
        </thead>
        <tbody>
          {receipts.map((envelope) => (
            <tr key={envelope.envelope_id}>
              <td>{envelope.envelope_id}</td>
              <td>{envelope.source_ref}</td>
              <td className="mono">{envelope.body_sha256}</td>
              <td>{envelope.synced_at}</td>
              <td>
                <button
                  type="button"
                  onClick={() => select(selected === envelope.envelope_id ? '' : envelope.envelope_id)}
                >
                  {selected === envelope.envelope_id ? 'Hide' : 'View'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected ? (
        <div className="mismatch-viewer">
          <h2>Mismatches — envelope {selected}</h2>
          {mismatchError === 'unavailable' ? (
            <p className="unavailable">
              Unavailable — the mismatch endpoint is not mounted for this deployment.
            </p>
          ) : mismatchError ? (
            <p className="error">{mismatchError}</p>
          ) : mismatches.length === 0 ? (
            <p>The server reported no mismatches for this envelope.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Expected</th>
                  <th>Observed</th>
                </tr>
              </thead>
              <tbody>
                {mismatches.map((mismatch) => (
                  <tr key={mismatch.mismatch_id}>
                    <td>{mismatch.field}</td>
                    <td className="mono">{mismatch.expected}</td>
                    <td className="mono">{mismatch.observed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="note">
            This is a viewer, not a resolver: mismatches are shown as the server reports
            them. Reconciliation is the receipt owner&apos;s decision, never this page&apos;s.
          </p>
        </div>
      ) : null}
    </section>
  );
}

export default ReceiptsPage;
